package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.models.*
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import com.example.borshi.web.nutritionist.ui.theme.*
import com.example.borshi.web.nutritionist.ui.components.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Helper extension for formatting doubles in Kotlin/JS
private fun Double.toFixed(decimals: Int): String = asDynamic().toFixed(decimals) as String

/**
 * Modern Patient Detail View with Enterprise Design
 * Features: Sticky header, responsive layout, design system, accessibility
 */
@Composable
fun PatientDetailView(
    patient: Patient,
    repository: NutritionistRepository,
    onBack: () -> Unit,
    onPatientUpdated: () -> Unit = {}
) {
    var currentTab by remember { mutableStateOf("profile") }
    var progressHistory by remember { mutableStateOf<List<PatientProgress>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var showEditModal by remember { mutableStateOf(false) }
    var showAddNoteModal by remember { mutableStateOf(false) }
    var showAssignPlanModal by remember { mutableStateOf(false) }
    var notesCount by remember { mutableStateOf(0) }
    var progressCount by remember { mutableStateOf(0) }
    var refreshTrigger by remember { mutableStateOf(0) }

    val scope = rememberCoroutineScope()

    // Load patient data
    LaunchedEffect(patient.patientId) {
        scope.launch {
            isLoading = true
            repository.getPatientProgress(patient.patientId, limit = 10)
                .onSuccess {
                    progressHistory = it
                    progressCount = it.size
                    isLoading = false
                    console.log("✅ Loaded ${it.size} progress records")
                }
                .onFailure {
                    error = it.message
                    isLoading = false
                    console.error("❌ Error loading progress:", it)
                }

            // Load notes count
            repository.getClinicalNotes(patient.patientId, limit = 100)
                .onSuccess { notesCount = it.size }
        }
    }

    // Main Container with responsive layout
    Div({
        style {
            minHeight(100.vh)
            backgroundColor(ColorTokens.surfaceMuted)
        }
    }) {
        // Sticky Header
        PatientHeader(
            patient = patient,
            onBack = onBack,
            onEdit = { showEditModal = true },
            onAssignPlan = { showAssignPlanModal = true },
            onAddNote = {
                currentTab = "notes"
                showAddNoteModal = true
            }
        )

        // Content Area
        Div({
            style {
                maxWidth(1400.px)
                property("margin", "0 auto")
                padding(Spacing.lg)

                // Responsive grid - using CSS property for media queries
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr")
                property("gap", Spacing.lg.toString())
            }
        }) {
            // Left Sidebar (Patient Summary) - sticky on desktop
            Div({
                style {
                    // Responsive: desktop sticky positioning
                    property("@media (min-width: 1024px)", """
                        position: sticky;
                        top: 88px;
                        align-self: start;
                    """)
                }
            }) {
                PatientSummaryRail(patient = patient, repository = repository)
            }

            // Right Content Area
            Div {
                // Sticky Tab Bar
                Div({
                    classes("sticky-tabs")
                    style {
                        backgroundColor(ColorTokens.surface)
                        borderRadius(BorderRadius.xl)
                        padding(Spacing.sm)
                        marginBottom(Spacing.lg)
                        property("box-shadow", ColorTokens.shadowSm)
                    }
                }) {
                    ModernTabBar(
                        tabs = listOf(
                            TabItem("profile", "Profile", null),
                            TabItem("plan", "Nutrition Plan", null),
                            TabItem("progress", "Progress", progressCount),
                            TabItem("notes", "Notes", notesCount)
                        ),
                        activeTab = currentTab,
                        onTabChange = { currentTab = it }
                    )
                }

                // Tab Content Area
                Div {
                    when (currentTab) {
                        "profile" -> ProfileTab(patient)
                        "plan" -> PlanTab(patient, repository)
                        "progress" -> ProgressTab(patient, progressHistory, isLoading, error)
                        "notes" -> NotesTab(
                            patient = patient,
                            repository = repository,
                            externalShowAddNote = showAddNoteModal,
                            onAddNoteModalChange = { showAddNoteModal = it }
                        )
                    }
                }
            }
        }

        // Edit Patient Modal
        if (showEditModal) {
            EditPatientModal(
                patient = patient,
                repository = repository,
                onClose = { showEditModal = false },
                onPatientUpdated = {
                    showEditModal = false
                    refreshTrigger++ // Trigger data reload
                    onPatientUpdated() // Notify parent
                    console.log("✅ Patient updated successfully")
                }
            )
        }

        // Assign Plan Modal
        if (showAssignPlanModal) {
            val currentNutritionistId = com.example.borshi.web.firebase.FirebaseService.getCurrentUserId()
                ?: patient.assignedNutritionistId
            AssignPlanModal(
                isOpen = showAssignPlanModal,
                repository = repository,
                nutritionistId = currentNutritionistId,
                preSelectedPatientId = patient.patientId,
                onClose = { showAssignPlanModal = false },
                onPlanAssigned = {
                    showAssignPlanModal = false
                    onPatientUpdated()
                    console.log("✅ Plan assigned successfully")
                }
            )
        }
    }
}

// ============== MODERN COMPONENTS ==============

/**
 * Sticky Patient Header with actions
 */
@Composable
private fun PatientHeader(
    patient: Patient,
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onAssignPlan: () -> Unit,
    onAddNote: () -> Unit
) {
    Div({
        classes("sticky-header")
        style {
            padding(Spacing.lg, Spacing.xl)
            backgroundColor(ColorTokens.surface)
            property("box-shadow", ColorTokens.shadowSm)
        }
    }) {
        Div({
            style {
                maxWidth(1400.px)
                property("margin", "0 auto")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                gap(Spacing.lg)
                property("flex-wrap", "wrap")
            }
        }) {
            // Left: Back + Patient Info
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                    flex(1)
                }
            }) {
                // Back button
                Button({
                    style {
                        padding(Spacing.sm)
                        backgroundColor(Color("transparent"))
                        border(0.px)
                        cursor("pointer")
                        borderRadius(BorderRadius.md)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        color(ColorTokens.textSecondary)
                        property("transition", "120ms ease-in-out")
                    }
                    classes("modern-button-ghost")
                    onClick { onBack() }
                }) {
                    Icon("←", IconSize.lg)
                }

                Avatar(name = patient.name, size = 40.px, imageUrl = patient.profileImage)

                Div {
                    H1({
                        style {
                            margin(0.px)
                            fontSize(Typography.sectionTitle)
                            fontWeight(Typography.semibold)
                            color(ColorTokens.textPrimary)
                        }
                    }) {
                        Text(patient.name)
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.sm)
                            alignItems(AlignItems.Center)
                            marginTop(4.px)
                        }
                    }) {
                        Chip(
                            label = "${patient.age} yrs",
                            variant = ChipVariant.DEFAULT,
                            small = true
                        )
                        Chip(
                            label = patient.gender,
                            variant = ChipVariant.DEFAULT,
                            small = true
                        )
                        Chip(
                            label = when {
                                patient.bmi < 18.5 -> "Underweight"
                                patient.bmi < 25 -> "Normal"
                                patient.bmi < 30 -> "Overweight"
                                else -> "Obese"
                            },
                            variant = when {
                                patient.bmi < 18.5 -> ChipVariant.WARNING
                                patient.bmi < 25 -> ChipVariant.SUCCESS
                                patient.bmi < 30 -> ChipVariant.WARNING
                                else -> ChipVariant.DANGER
                            },
                            small = true
                        )
                        Chip(
                            label = patient.status.name,
                            variant = if (patient.status == PatientStatus.ACTIVE)
                                ChipVariant.SUCCESS else ChipVariant.DEFAULT,
                            small = true
                        )
                    }
                }
            }

            // Right: Action Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    property("flex-wrap", "wrap")
                }
                classes("hide-mobile")
            }) {
                ModernButton(
                    text = "Edit Profile",
                    variant = ButtonVariant.SECONDARY,
                    size = ButtonSize.SM,
                    onClick = onEdit
                )
                ModernButton(
                    text = "Assign Plan",
                    variant = ButtonVariant.PRIMARY,
                    size = ButtonSize.SM,
                    onClick = onAssignPlan
                )
                ModernButton(
                    text = "Add Note",
                    variant = ButtonVariant.GHOST,
                    size = ButtonSize.SM,
                    onClick = onAddNote
                )
            }
        }
    }
}

/**
 * Patient Summary Rail (Left Sidebar)
 */
@Composable
private fun PatientSummaryRail(
    patient: Patient,
    repository: NutritionistRepository
) {
    Div {
        ModernCard {
            // Key Vitals
            Div({
                style {
                    marginBottom(Spacing.lg)
                    paddingBottom(Spacing.lg)
                    property("border-bottom", "1px solid ${ColorTokens.border}")
                }
            }) {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Text("Key Vitals")
                }

                VitalMetric("Weight", "${patient.weight} kg", "⚖️")
                VitalMetric("Height", "${patient.height} cm", "📏")
                VitalMetric("BMI", patient.bmi.toFixed(1), "📊")
                VitalMetric("Adherence", "${patient.adherenceScore}%", "✓")
            }

            // Contact
            Div({
                style {
                    marginBottom(Spacing.lg)
                    paddingBottom(Spacing.lg)
                    property("border-bottom", "1px solid ${ColorTokens.border}")
                }
            }) {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Text("Contact")
                }

                ContactItem("Email", patient.email, "📧")
                ContactItem("Phone", patient.phone, "📱")
            }

            // Last Updated
            Div({
                style {
                    fontSize(Typography.caption)
                    color(ColorTokens.textTertiary)
                }
            }) {
                Text("Last updated: ${formatDate(patient.updatedAt)}")
            }
        }
    }
}

@Composable
private fun VitalMetric(label: String, value: String, icon: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(Spacing.sm, 0.px)
        }
    }) {
        Span({
            style {
                fontSize(Typography.bodySmall)
                color(ColorTokens.textSecondary)
            }
        }) {
            Text("$icon $label")
        }
        Span({
            style {
                fontSize(Typography.body)
                fontWeight(Typography.semibold)
                color(ColorTokens.textPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun ContactItem(label: String, value: String, icon: String) {
    Div({
        style {
            marginBottom(Spacing.sm)
        }
    }) {
        Div({
            style {
                fontSize(Typography.caption)
                color(ColorTokens.textTertiary)
                marginBottom(4.px)
            }
        }) {
            Text("$icon $label")
        }
        Div({
            style {
                fontSize(Typography.bodySmall)
                color(ColorTokens.textPrimary)
            }
        }) {
            Text(value)
        }
    }
}

/**
 * Modern Tab Bar with counts
 */
data class TabItem(val id: String, val label: String, val count: Int?)

@Composable
private fun ModernTabBar(
    tabs: List<TabItem>,
    activeTab: String,
    onTabChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(Spacing.xs)
            position(Position.Relative)
        }
    }) {
        tabs.forEach { tab ->
            Button({
                style {
                    padding(12.px, Spacing.lg)
                    backgroundColor(if (activeTab == tab.id)
                        ColorTokens.primaryLight else Color("transparent"))
                    color(if (activeTab == tab.id)
                        ColorTokens.primary else ColorTokens.textSecondary)
                    border(0.px)
                    borderRadius(BorderRadius.lg)
                    cursor("pointer")
                    fontSize(Typography.bodySmall)
                    fontWeight(if (activeTab == tab.id) Typography.semibold else Typography.regular)
                    property("transition", "120ms ease-in-out")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.xs)
                }
                classes("modern-button-ghost")
                onClick { onTabChange(tab.id) }
            }) {
                Text(tab.label)
                tab.count?.let {
                    Span({
                        style {
                            backgroundColor(if (activeTab == tab.id)
                                ColorTokens.primary else ColorTokens.surfaceMuted)
                            color(if (activeTab == tab.id)
                                ColorTokens.textInverse else ColorTokens.textSecondary)
                            padding(2.px, 6.px)
                            borderRadius(BorderRadius.full)
                            fontSize(Typography.tiny)
                            fontWeight(Typography.medium)
                        }
                    }) {
                        Text(it.toString())
                    }
                }
            }
        }
    }
}

@Composable
private fun ContactInfo(icon: String, label: String, value: String) {
    Div {
        Div({
            style {
                fontSize(12.px)
                color(Color("#666"))
                marginBottom(4.px)
            }
        }) {
            Text("$icon $label")
        }
        Div({
            style {
                fontSize(14.px)
                color(Color("#333"))
                fontWeight("500")
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun MetricCard(icon: String, label: String, value: String, color: String) {
    Card {
        Div({ style { textAlign("center") } }) {
            Div({ style { fontSize(32.px); marginBottom(8.px) } }) {
                Text(icon)
            }
            Div({
                style {
                    fontSize(12.px)
                    color(Color("#666"))
                    marginBottom(4.px)
                }
            }) {
                Text(label)
            }
            Div({
                style {
                    fontSize(24.px)
                    fontWeight("bold")
                    color(Color(color))
                }
            }) {
                Text(value)
            }
        }
    }
}

@Composable
private fun InfoSection(title: String, icon: String, items: List<String>, emptyMessage: String) {
    Card {
        H4({
            style {
                margin(0.px)
                fontSize(16.px)
                marginBottom(12.px)
                color(Color("#333"))
            }
        }) {
            Text("$icon $title")
        }

        if (items.isEmpty()) {
            P({
                style {
                    margin(0.px)
                    color(Color("#999"))
                    fontSize(14.px)
                    fontStyle("italic")
                }
            }) {
                Text(emptyMessage)
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                }
            }) {
                items.forEach { item ->
                    Div({
                        style {
                            padding(8.px, 12.px)
                            backgroundColor(Color("#f5f5f5"))
                            borderRadius(6.px)
                            fontSize(14.px)
                        }
                    }) {
                        Text("• $item")
                    }
                }
            }
        }
    }
}

@Composable
private fun ProgressCard(progress: PatientProgress) {
    Card {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "auto 1fr")
                gap(16.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Date
            Div({
                style {
                    textAlign("center")
                    padding(12.px)
                    backgroundColor(Color("#f5f5f5"))
                    borderRadius(8.px)
                }
            }) {
                Div({ style { fontSize(20.px); fontWeight("bold"); color(Color("#2196F3")) } }) {
                    Text(formatDate(progress.recordDate))
                }
            }

            // Metrics
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(120px, 1fr))")
                    gap(12.px)
                }
            }) {
                ProgressMetric("Weight", "${progress.weight} kg")
                ProgressMetric("BMI", progress.bmi.toString())
                ProgressMetric("Adherence", "${progress.adherenceScore}%")
                ProgressMetric("Energy", "${progress.energyLevel}/5")
            }
        }

        if (progress.notes.isNotBlank()) {
            Div({
                style {
                    marginTop(12.px)
                    paddingTop(12.px)
                    property("border-top", "1px solid #eee")
                    fontSize(14.px)
                    color(Color("#666"))
                }
            }) {
                Text("📝 ${progress.notes}")
            }
        }
    }
}

@Composable
private fun ProgressMetric(label: String, value: String) {
    Div {
        Div({ style { fontSize(12.px); color(Color("#999")); marginBottom(2.px) } }) {
            Text(label)
        }
        Div({ style { fontSize(16.px); fontWeight("600"); color(Color("#333")) } }) {
            Text(value)
        }
    }
}

// Profile Tab - Shows detailed patient information with modern design
@Composable
private fun ProfileTab(patient: Patient) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
        }
    }) {
        // Health Goals
        if (patient.healthGoals.isNotEmpty()) {
            ModernCard {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Icon("🎯", IconSize.md)
                    Text(" Health Goals")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(Spacing.sm)
                    }
                }) {
                    patient.healthGoals.forEach { goal ->
                        Div({
                            style {
                                padding(Spacing.sm, Spacing.md)
                                backgroundColor(ColorTokens.surfaceMuted)
                                borderRadius(BorderRadius.md)
                                fontSize(Typography.bodySmall)
                                color(ColorTokens.textPrimary)
                            }
                        }) {
                            Text("• $goal")
                        }
                    }
                }
            }
        }

        // Health Conditions
        if (patient.healthConditions.isNotEmpty()) {
            ModernCard {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Icon("🏥", IconSize.md)
                    Text(" Health Conditions")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(Spacing.sm)
                    }
                }) {
                    patient.healthConditions.forEach { condition ->
                        Chip(label = condition, variant = ChipVariant.WARNING)
                    }
                }
            }
        }

        // Allergies
        if (patient.allergies.isNotEmpty()) {
            ModernCard {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Icon("⚠️", IconSize.md)
                    Text(" Allergies")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(Spacing.sm)
                    }
                }) {
                    patient.allergies.forEach { allergy ->
                        Chip(label = allergy, variant = ChipVariant.DANGER)
                    }
                }
            }
        }

        // Dietary Restrictions
        if (patient.dietaryRestrictions.isNotEmpty()) {
            ModernCard {
                H3({
                    style {
                        fontSize(Typography.subsectionTitle)
                        fontWeight(Typography.semibold)
                        margin(0.px)
                        marginBottom(Spacing.md)
                        color(ColorTokens.textPrimary)
                    }
                }) {
                    Icon("🥗", IconSize.md)
                    Text(" Dietary Restrictions")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(Spacing.sm)
                    }
                }) {
                    patient.dietaryRestrictions.forEach { restriction ->
                        Chip(label = restriction, variant = ChipVariant.INFO)
                    }
                }
            }
        }

        // Empty state
        if (patient.healthGoals.isEmpty() && patient.healthConditions.isEmpty() &&
            patient.allergies.isEmpty() && patient.dietaryRestrictions.isEmpty()) {
            EmptyState(
                icon = "📋",
                title = "No Profile Information",
                message = "Click 'Edit Profile' to add health goals, conditions, and preferences"
            )
        }
    }
}

// Keep old InfoSection for backwards compatibility temporarily
@Composable
private fun InfoSection_Old(
    title: String,
    icon: String,
    items: List<String>,
    emptyMessage: String
) {
    Card {
        H4({
            style {
                margin(0.px)
                fontSize(16.px)
                marginBottom(12.px)
                color(Color("#333"))
            }
        }) {
            Text("$icon $title")
        }

        if (items.isEmpty()) {
            P({
                style {
                    margin(0.px)
                    color(Color("#999"))
                    fontSize(14.px)
                    fontStyle("italic")
                }
            }) {
                Text(emptyMessage)
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                }
            }) {
                items.forEach { item ->
                    Div({
                        style {
                            padding(8.px, 12.px)
                            backgroundColor(Color("#f5f5f5"))
                            borderRadius(6.px)
                            fontSize(14.px)
                        }
                    }) {
                        Text("• $item")
                    }
                }
            }
        }
    }
}

// Profile Tab Old Version - Shows detailed patient information
@Composable
private fun ProfileTab_Old(patient: Patient) {
    Div {
        // Metrics Grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                gap(16.px)
                marginBottom(24.px)
            }
        }) {
            MetricCard("⚖️", "Current Weight", "${patient.weight} kg", "#2196F3")
            MetricCard("📏", "Height", "${patient.height} cm", "#4CAF50")
            MetricCard("📊", "BMI", "${(patient.bmi * 10).toInt() / 10.0}", getBMIColor(patient.bmi))
            MetricCard("✅", "Adherence", "${patient.adherenceScore}%", "#9C27B0")
        }

        // Health Information Sections
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(350px, 1fr))")
                gap(20.px)
            }
        }) {
            InfoSection_Old(
                title = "Health Goals",
                icon = "🎯",
                items = patient.healthGoals,
                emptyMessage = "No health goals set"
            )

            InfoSection_Old(
                title = "Health Conditions",
                icon = "🏥",
                items = patient.healthConditions,
                emptyMessage = "No health conditions reported"
            )

            InfoSection_Old(
                title = "Allergies",
                icon = "⚠️",
                items = patient.allergies,
                emptyMessage = "No allergies reported"
            )

            InfoSection(
                title = "Dietary Restrictions",
                icon = "🚫",
                items = patient.dietaryRestrictions,
                emptyMessage = "No dietary restrictions"
            )
        }
    }
}

// Plan Tab - Shows assigned nutrition plan
@Composable
private fun PlanTab(patient: Patient, repository: NutritionistRepository) {
    var planDetails by remember { mutableStateOf<NutritionistBasePlan?>(null) }
    var isLoadingPlan by remember { mutableStateOf(false) }
    var showPlanDetails by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    if (patient.currentPlanId != null) {
        Card {
            H3({ style { fontSize(20.px); marginBottom(16.px); color(Color("#333")) } }) {
                Text("📋 Current Nutrition Plan")
            }

            Div({
                style {
                    padding(16.px)
                    backgroundColor(Color("#E8F5E9"))
                    borderRadius(8.px)
                    marginBottom(16.px)
                }
            }) {
                Div({ style { fontSize(16.px); fontWeight("600"); color(Color("#2E7D32")); marginBottom(8.px) } }) {
                    Text("Active Plan")
                }
                Div({ style { fontSize(14.px); color(Color("#666")) } }) {
                    Text("Plan ID: ${patient.currentPlanId}")
                }
                if (patient.planStartDate > 0) {
                    Div({ style { fontSize(14.px); color(Color("#666")); marginTop(4.px) } }) {
                        Text("Started: ${formatDate(patient.planStartDate)}")
                    }
                }
            }

            // Show plan details if loaded
            if (showPlanDetails && planDetails != null) {
                Div({
                    style {
                        padding(16.px)
                        backgroundColor(Color("#F5F5F5"))
                        borderRadius(8.px)
                        marginBottom(16.px)
                    }
                }) {
                    H4({ style { fontSize(16.px); marginBottom(12.px); color(Color("#333")) } }) {
                        Text(planDetails!!.planName)
                    }
                    P({ style { fontSize(14.px); color(Color("#666")); marginBottom(8.px) } }) {
                        Text(planDetails!!.description)
                    }

                    // Nutritional Targets
                    Div({ style { marginTop(12.px) } }) {
                        Div({ style { fontSize(14.px); fontWeight("600"); marginBottom(6.px) } }) {
                            Text("Nutritional Targets:")
                        }
                        Div({ style { fontSize(13.px); color(Color("#666")) } }) {
                            Text("• Protein: ${planDetails!!.nutritionalTargets.proteinPerWeek}g/week")
                        }
                        Div({ style { fontSize(13.px); color(Color("#666")) } }) {
                            Text("• Calories: ${planDetails!!.nutritionalTargets.caloriesPerDay} kcal/day")
                        }
                        Div({ style { fontSize(13.px); color(Color("#666")) } }) {
                            Text("• Omega-3: ${planDetails!!.nutritionalTargets.omega3PerWeek}g/week")
                        }
                    }
                }
            }

            Button({
                style {
                    padding(10.px, 20.px)
                    backgroundColor(if (isLoadingPlan) Color("#ccc") else Color("#FF9800"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(6.px)
                    cursor(if (isLoadingPlan) "wait" else "pointer")
                    fontSize(14.px)
                    fontWeight("600")
                }
                if (isLoadingPlan) {
                    attr("disabled", "disabled")
                }
                onClick {
                    if (!isLoadingPlan) {
                        scope.launch {
                            isLoadingPlan = true
                            repository.getBasePlan(patient.currentPlanId!!)
                                .onSuccess {
                                    planDetails = it
                                    showPlanDetails = true
                                    isLoadingPlan = false
                                    console.log("✅ Loaded plan details: ${it.planName}")
                                }
                                .onFailure {
                                    console.error("❌ Error loading plan details:", it)
                                    isLoadingPlan = false
                                }
                        }
                    }
                }
            }) {
                Text(if (isLoadingPlan) "Loading..." else if (showPlanDetails) "Hide Plan Details" else "View Plan Details")
            }
        }
    } else {
        EmptyState(
            icon = "📋",
            title = "No Plan Assigned",
            message = "This patient doesn't have a nutrition plan yet. Assign one to get started."
        )
    }
}

// Progress Tab - Shows patient progress history
@Composable
private fun ProgressTab(
    patient: Patient,
    progressHistory: List<PatientProgress>,
    isLoading: Boolean,
    error: String?
) {
    when {
        isLoading -> LoadingSpinner()
        error != null -> ErrorMessage(error)
        progressHistory.isEmpty() -> EmptyState(
            icon = "📊",
            title = "No Progress Data",
            message = "Start tracking progress to see charts and trends here."
        )
        else -> {
            Div {
                H3({ style { fontSize(20.px); marginBottom(16.px); color(Color("#333")) } }) {
                    Text("📈 Progress History (${progressHistory.size} records)")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    progressHistory.forEach { progress ->
                        ProgressCard(progress)
                    }
                }
            }
        }
    }
}

// Notes Tab - Shows clinical notes with add functionality
@Composable
private fun NotesTab(
    patient: Patient,
    repository: NutritionistRepository,
    externalShowAddNote: Boolean = false,
    onAddNoteModalChange: (Boolean) -> Unit = {}
) {
    var notes by remember { mutableStateOf<List<com.example.borshi.web.nutritionist.repository.ClinicalNote>>(emptyList()) }
    var showAddNoteModal by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(true) }
    var refreshTrigger by remember { mutableStateOf(0) }

    val scope = rememberCoroutineScope()

    // Handle external trigger - only react when it becomes true
    LaunchedEffect(externalShowAddNote) {
        if (externalShowAddNote && !showAddNoteModal) {
            showAddNoteModal = true
            onAddNoteModalChange(false) // Reset external trigger immediately
        }
    }

    // Load notes from Firebase
    LaunchedEffect(patient.patientId, refreshTrigger) {
        scope.launch {
            isLoading = true
            repository.getClinicalNotes(patient.patientId, limit = 20)
                .onSuccess {
                    notes = it
                    isLoading = false
                    console.log("✅ Loaded ${it.size} clinical notes")
                }
                .onFailure {
                    console.error("❌ Error loading notes:", it)
                    isLoading = false
                }
        }
    }

    Div {
        // Header with Add Button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(20.px)
            }
        }) {
            H3({ style { margin(0.px); fontSize(20.px); color(Color("#333")) } }) {
                Text("📝 Clinical Notes (${notes.size})")
            }

            Button({
                style {
                    padding(10.px, 20.px)
                    backgroundColor(Color("#4CAF50"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(6.px)
                    cursor("pointer")
                    fontSize(14.px)
                    fontWeight("600")
                }
                onClick { showAddNoteModal = true }
            }) {
                Text("+ Add Note")
            }
        }

        when {
            isLoading -> LoadingSpinner()
            notes.isEmpty() -> EmptyState(
                icon = "📝",
                title = "No Clinical Notes",
                message = "Add consultation notes, observations, and feedback to track patient progress."
            )
            else -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    notes.forEach { note ->
                        ClinicalNoteCard(note)
                    }
                }
            }
        }

        // Add Note Modal
        if (showAddNoteModal) {
            AddNoteModal(
                patient = patient,
                repository = repository,
                onClose = { showAddNoteModal = false },
                onNoteAdded = {
                    showAddNoteModal = false
                    refreshTrigger++ // Trigger notes refresh
                }
            )
        }
    }
}

@Composable
private fun ClinicalNoteCard(note: com.example.borshi.web.nutritionist.repository.ClinicalNote) {
    Card {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Span({
                    style {
                        padding(4.px, 12.px)
                        backgroundColor(getNoteTypeColor(note.noteType))
                        color(Color.white)
                        borderRadius(12.px)
                        fontSize(12.px)
                        fontWeight("600")
                    }
                }) {
                    Text(note.noteType)
                }

                Span({ style { fontSize(13.px); color(Color("#999")) } }) {
                    Text(formatFullDate(note.createdAt))
                }
            }
        }

        P({
            style {
                margin(0.px)
                fontSize(14.px)
                lineHeight("1.6")
                color(Color("#333"))
                marginBottom(12.px)
            }
        }) {
            Text(note.content)
        }

        if (note.tags.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(6.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                note.tags.forEach { tag ->
                    Span({
                        style {
                            fontSize(11.px)
                            padding(4.px, 10.px)
                            backgroundColor(Color("#E3F2FD"))
                            color(Color("#1976D2"))
                            borderRadius(10.px)
                        }
                    }) {
                        Text("#$tag")
                    }
                }
            }
        }
    }
}

@Composable
private fun AddNoteModal(
    patient: Patient,
    repository: NutritionistRepository,
    onClose: () -> Unit,
    onNoteAdded: () -> Unit
) {
    var noteType by remember { mutableStateOf("General") }
    var content by remember { mutableStateOf("") }
    var tags by remember { mutableStateOf("") }
    var isSaving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(600.px)
                width(100.percent)
                padding(24.px)
            }
            onClick { it.stopPropagation() }
        }) {
            H3({ style { margin(0.px); marginBottom(20.px); fontSize(20.px); color(Color("#333")) } }) {
                Text("✍️ Add Clinical Note")
            }

            // Error Message
            if (error != null) {
                Div({
                    style {
                        padding(12.px)
                        backgroundColor(Color("#FFEBEE"))
                        color(Color("#C62828"))
                        borderRadius(6.px)
                        marginBottom(16.px)
                        fontSize(14.px)
                    }
                }) {
                    Text("❌ $error")
                }
            }

            // Note Type Selector
            Div({ style { marginBottom(16.px) } }) {
                Div({
                    attr("for", "noteType")
                    style {
                        display(DisplayStyle.Block)
                        marginBottom(8.px)
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color("#333"))
                    }
                }) {
                    Text("Note Type")
                }

                Select({
                    attr("id", "noteType")
                    style {
                        width(100.percent)
                        padding(10.px)
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        fontSize(14.px)
                    }
                    onChange { noteType = it.value ?: "General" }
                }) {
                    Option("General") { Text("General") }
                    Option("Consultation") { Text("Consultation") }
                    Option("Observation") { Text("Observation") }
                    Option("Feedback") { Text("Feedback") }
                }
            }

            // Content TextArea
            Div({ style { marginBottom(16.px) } }) {
                Div({
                    attr("for", "noteContent")
                    style {
                        display(DisplayStyle.Block)
                        marginBottom(8.px)
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color("#333"))
                    }
                }) {
                    Text("Note Content *")
                }

                TextArea(attrs = {
                    attr("id", "noteContent")
                    value(content)
                    onInput { content = it.value }
                    attr("placeholder", "Enter clinical notes, observations, or feedback...")
                    attr("rows", "5")
                    style {
                        width(100.percent)
                        padding(10.px)
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        fontSize(14.px)
                        fontFamily("inherit")
                    }
                })
            }

            // Tags Input
            Div({ style { marginBottom(20.px) } }) {
                Div({
                    attr("for", "noteTags")
                    style {
                        display(DisplayStyle.Block)
                        marginBottom(8.px)
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color("#333"))
                    }
                }) {
                    Text("Tags (comma-separated)")
                }

                Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                    attr("id", "noteTags")
                    value(tags)
                    onInput { tags = it.value }
                    style {
                        width(100.percent)
                        padding(10.px)
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        fontSize(14.px)
                    }
                    attr("placeholder", "e.g., Weight Loss, Progress, Diet Change")
                }
            }

            // Action Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(Color("transparent"))
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        cursor("pointer")
                        fontSize(14.px)
                    }
                    onClick { onClose() }
                }) {
                    Text("Cancel")
                }

                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(if (content.isBlank() || isSaving) Color("#ccc") else Color("#4CAF50"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(6.px)
                        cursor(if (content.isBlank() || isSaving) "not-allowed" else "pointer")
                        fontSize(14.px)
                        fontWeight("600")
                    }
                    if (content.isBlank() || isSaving) {
                        attr("disabled", "disabled")
                    }
                    onClick {
                        if (content.isNotBlank() && !isSaving) {
                            scope.launch {
                                isSaving = true
                                error = null

                                val tagList = tags.split(",")
                                    .map { it.trim() }
                                    .filter { it.isNotBlank() }

                                val note = com.example.borshi.web.nutritionist.repository.ClinicalNote(
                                    patientId = patient.patientId,
                                    nutritionistId = "nutritionist_001", // TODO: Get from auth
                                    nutritionistName = "Dr. Sarah Johnson", // TODO: Get from profile
                                    noteType = noteType,
                                    content = content,
                                    tags = tagList,
                                    createdAt = kotlin.js.Date.now().toLong(),
                                    updatedAt = kotlin.js.Date.now().toLong()
                                )

                                repository.addClinicalNote(note)
                                    .onSuccess {
                                        console.log("✅ Note saved successfully: $it")
                                        isSaving = false
                                        onNoteAdded()
                                    }
                                    .onFailure {
                                        console.error("❌ Error saving note:", it)
                                        error = "Failed to save note: ${it.message}"
                                        isSaving = false
                                    }
                            }
                        }
                    }
                }) {
                    Text(if (isSaving) "Saving..." else "Save Note")
                }
            }
        }
    }
}

private fun getNoteTypeColor(type: String): CSSColorValue {
    return when (type) {
        "Consultation" -> Color("#2196F3")
        "Observation" -> Color("#4CAF50")
        "Feedback" -> Color("#FF9800")
        else -> Color("#9C27B0")
    }
}

private fun formatFullDate(timestamp: Long): String {
    val date = kotlin.js.Date(timestamp)
    val day = date.getDate().toString().padStart(2, '0')
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    val year = date.getFullYear()
    val hours = date.getHours().toString().padStart(2, '0')
    val minutes = date.getMinutes().toString().padStart(2, '0')
    return "$day/$month/$year at $hours:$minutes"
}

private fun getBMIColor(bmi: Double): String {
    return when {
        bmi < 18.5 -> "#FF9800" // Underweight
        bmi < 25.0 -> "#4CAF50" // Normal
        bmi < 30.0 -> "#FF9800" // Overweight
        else -> "#F44336" // Obese
    }
}

private fun formatDate(timestamp: Long): String {
    val date = kotlin.js.Date(timestamp)
    val day = date.getDate().toString().padStart(2, '0')
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    return "$day/$month"
}
